/**
 * Swatch Image Filter
 * Filters product gallery images based on the selected color swatch.
 * Only images whose alt text contains the selected color (exact match) are shown.
 * Images with no color match are treated as "general" and always visible.
 * If no images match the selected color, all images are shown as fallback.
 */
class SwatchImageFilter {
  constructor() {
    this.productInfo = document.querySelector("product-info");
    if (!this.productInfo) return;
    this.init();
  }

  init() {
    this.colors = this.getColorOptions();
    if (!this.colors || this.colors.length === 0) return;

    // Sort colors by length descending for longest-match-first extraction
    this.sortedColors = [...this.colors]
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);

    // Disable the theme's native variant→image jump to prevent flicker
    if (typeof this.productInfo.updateMedia === "function") {
      this.productInfo.updateMedia = () => {};
    }

    this.lastSelectedColor = null;

    this.patchProductImagesNavigation();
    this.assignColorsToImages();

    // Listen for swatch radio changes (fires instantly, before Shopify AJAX)
    document.addEventListener("change", (e) => {
      const radio = e.target;
      if (!radio.matches('input[type="radio"]')) return;
      const fieldset = radio.closest("fieldset.product-form__input");
      if (!fieldset) return;
      const legend = fieldset.querySelector("legend");
      if (this.isColorLegend(legend)) {
        this.filterImages(radio.value);
      }
    });

    // Fallback: also listen to the theme's variant change pub/sub
    subscribe(
      theme.PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this),
    );

    // Apply initial filter for the pre-selected swatch
    this.filterImages(this.getSelectedColor());
  }

  /**
   * Monkey-patch the <product-images> element's arrow navigation
   * so it skips over slides we've hidden with .is-hidden-by-filter.
   */
  patchProductImagesNavigation() {
    document.querySelectorAll("product-images").forEach((el) => {
      if (el._patchedBySwatchFilter) return;

      el.getNextMediaId = function () {
        const current = this.getCurrentMedia();
        const visible = Array.from(
          this.querySelectorAll("[data-product-media-list] > *"),
        ).filter((s) => !s.classList.contains("is-hidden-by-filter"));
        if (!visible.length) return null;
        const idx = visible.indexOf(current);
        return visible[(idx + 1) % visible.length]?.getAttribute(
          "data-media-id",
        );
      };

      el.getPreviousMediaId = function () {
        const current = this.getCurrentMedia();
        const visible = Array.from(
          this.querySelectorAll("[data-product-media-list] > *"),
        ).filter((s) => !s.classList.contains("is-hidden-by-filter"));
        if (!visible.length) return null;
        const idx = visible.indexOf(current);
        const prev = idx <= 0 ? visible.length - 1 : idx - 1;
        return visible[prev]?.getAttribute("data-media-id");
      };

      el._patchedBySwatchFilter = true;
    });
  }

  /** Check whether a <legend> element refers to a color option. */
  isColorLegend(legend) {
    if (!legend) return false;
    const text = legend.textContent.toLowerCase();
    return text.includes("color") || text.includes("colour");
  }

  /** Return an array of all color option values (e.g. ["Bullitt Blue", "Sage Green"]). */
  getColorOptions() {
    for (const fieldset of document.querySelectorAll(
      "fieldset.product-form__input",
    )) {
      if (!this.isColorLegend(fieldset.querySelector("legend"))) continue;
      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      if (inputs.length) return Array.from(inputs).map((el) => el.value);
    }

    // Fallback: dropdown selects
    for (const select of document.querySelectorAll("variant-selects select")) {
      const name = (select.getAttribute("name") || "").toLowerCase();
      if (name.includes("color") || name.includes("colour")) {
        return Array.from(select.options).map((opt) => opt.value);
      }
    }

    return null;
  }

  /** Return the currently selected color value. */
  getSelectedColor() {
    for (const fieldset of document.querySelectorAll(
      "fieldset.product-form__input",
    )) {
      if (!this.isColorLegend(fieldset.querySelector("legend"))) continue;
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;
    }

    for (const select of document.querySelectorAll("variant-selects select")) {
      const name = (select.getAttribute("name") || "").toLowerCase();
      if (name.includes("color") || name.includes("colour")) {
        return select.value;
      }
    }

    return null;
  }

  /** Tag each media element with its matched color via data-assigned-color. */
  assignColorsToImages() {
    document
      .querySelectorAll(
        "[data-product-media-list] > *, [data-thumbs-slider] > *",
      )
      .forEach((container) => {
        const img = container.querySelector("img");
        const altText = img
          ? (img.getAttribute("alt") || "").toLowerCase()
          : "";

        if (!altText) {
          container.dataset.assignedColor = "general";
          return;
        }

        let matched = "general";
        for (const color of this.sortedColors) {
          if (altText.includes(color.toLowerCase())) {
            matched = color;
            break;
          }
        }
        container.dataset.assignedColor = matched;
      });
  }

  handleVariantChange(event) {
    if (!event.data?.variant) return;

    const colorIndex = this.getColorOptionIndex(event.data.html);
    let selectedColor =
      colorIndex !== -1 ? event.data.variant[`option${colorIndex + 1}`] : null;

    this.filterImages(selectedColor || this.getSelectedColor());
  }

  /** Find the 0-based option index for the color option. */
  getColorOptionIndex(doc) {
    doc = doc || document;

    // Prefer data-option-position attribute
    for (const wrapper of doc.querySelectorAll("[data-option-position]")) {
      if (!this.isColorLegend(wrapper.querySelector("legend"))) continue;
      const pos = parseInt(wrapper.getAttribute("data-option-position"), 10);
      if (!isNaN(pos)) return pos - 1;
    }

    // Fallback: fieldset index
    const fieldsets = doc.querySelectorAll("fieldset.product-form__input");
    for (let i = 0; i < fieldsets.length; i++) {
      if (this.isColorLegend(fieldsets[i].querySelector("legend"))) return i;
    }

    // Fallback: select index
    const selects = doc.querySelectorAll("variant-selects select");
    for (let i = 0; i < selects.length; i++) {
      const name = (selects[i].getAttribute("name") || "").toLowerCase();
      if (name.includes("color") || name.includes("colour")) return i;
    }

    return -1;
  }

  /** Show/hide media elements based on the selected color. */
  filterImages(selectedColor) {
    if (!selectedColor) return;

    const colorLower = selectedColor.toLowerCase();
    const isInitialLoad = this.lastSelectedColor === null;
    const colorChanged = this.lastSelectedColor !== colorLower;

    if (!colorChanged && !isInitialLoad) return;
    this.lastSelectedColor = colorLower;

    const allMedia = document.querySelectorAll(
      "[data-product-media-list] > *, [data-thumbs-slider] > *",
    );

    // Check if any images are tagged with this exact color
    const hasExactMatch = Array.from(allMedia).some(
      (el) => el.dataset.assignedColor?.toLowerCase() === colorLower,
    );

    let firstMatch = null;

    allMedia.forEach((el) => {
      const assigned = el.dataset.assignedColor;
      if (!assigned) return;

      const isMatch = assigned.toLowerCase() === colorLower;
      const shouldShow = !hasExactMatch || assigned === "general" || isMatch;

      if (shouldShow) {
        el.style.setProperty("display", "block", "important");
        el.classList.remove("is-hidden-by-filter");
        if (isMatch && !firstMatch) firstMatch = el;
      } else {
        el.style.setProperty("display", "none", "important");
        el.classList.add("is-hidden-by-filter");
      }
    });

    // Trigger a resize so the theme recalculates gallery height
    window.dispatchEvent(new Event("resize"));

    // Auto-select the first matching image when color changes or on initial load
    if (firstMatch && colorChanged) {
      const mediaId =
        firstMatch.getAttribute("data-media-id") ||
        firstMatch
          .querySelector("[data-media-id]")
          ?.getAttribute("data-media-id");

      if (mediaId) {
        const target = document.querySelector("product-images") || firstMatch;
        target.dispatchEvent(
          new CustomEvent("theme:media:select", {
            bubbles: true,
            detail: { id: mediaId },
          }),
        );
      }
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new SwatchImageFilter();
});
